// Singleton Auth0Client wrapper.
//
// Why a singleton? Both LoginView.vue (initial login flow) and the global
// axios interceptor in store.ts (silent token regeneration on 401s) need
// the same Auth0Client instance so they share its localStorage cache and
// refresh-token state. Creating a second client elsewhere causes split
// state and intermittent "user not authenticated" errors even when Auth0
// actually has a valid session.

import { createAuth0Client, Auth0Client } from "@auth0/auth0-spa-js";
import { AppSettings } from "@/store";

let clientPromise: Promise<Auth0Client> | null = null;

// Same helper as notify-web-upgrade Auth0Login.buildRedirectUri().
// Init and loginWithRedirect must use the identical URI — the SDK caches
// tokens against it, and a bare origin here vs a /auth0login href at login
// time causes getTokenSilently() to fail after the callback.
export function buildRedirectUri(): string {
  const origin = window.location.origin.toLowerCase();
  const path = window.location.pathname.toLowerCase();
  if (path === "/login" || path.includes("unread-notifications")) {
    return origin + "/auth0login" + window.location.search;
  }
  return origin + window.location.pathname + window.location.search;
}

export function getAuth0Client(): Promise<Auth0Client> {
  if (!clientPromise) {
    clientPromise = createAuth0Client({
      domain: AppSettings.auth0Domain,
      clientId: AppSettings.auth0CliientId,
      cacheLocation: "localstorage",
      useRefreshTokens: true,
      useCookiesForTransactions: false,
      // Make sure we never silently fall back to a hidden iframe/cookie
      // refresh — if the refresh token is gone we want to KNOW (so the
      // interceptor force-logouts) rather than silently succeeding with a
      // different cache key. Without this, scope mismatches between login
      // and getTokenSilently can be papered over by an iframe call and
      // then break unpredictably later. (Same lesson the web codebase
      // learned in src/lib/auth0.ts.)
      useRefreshTokensFallback: false,
      sessionCheckExpiryDays: 30,
      leeway: 300,
      authorizationParams: {
        redirect_uri: buildRedirectUri(),
        // Must match the scope set by LoginView.vue#loginnew(). The Auth0
        // SDK keys its refresh-token cache by (audience, scope), so a
        // mismatch here makes getTokenSilently() report "Missing Refresh
        // Token" even when the user has a perfectly valid Auth0 session —
        // exactly the failure that breaks silent JWT regen on the mobile
        // app after a server key rotation.
        scope: "openid profile email read:user offline_access",
      },
    });
  }
  return clientPromise;
}

// Returns a fresh Auth0 access_token if the user is still authenticated with
// Auth0 (silently using the refresh token if the cached access_token expired).
// Throws if the user is no longer authenticated with Auth0 — callers should
// treat that as "force logout".
export async function getFreshAuth0AccessToken(): Promise<string> {
  const client = await getAuth0Client();
  const token = await client.getTokenSilently();
  // Keep localStorage in sync so any code still reading it directly sees
  // the latest token.
  window.localStorage.setItem("access_token", token);
  return token;
}

// Returns the current Auth0 user (cached). Used to read email for the
// generatecustomtokeninauth0 call.
export async function getAuth0User(): Promise<any> {
  const client = await getAuth0Client();
  return client.getUser();
}
